/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'

import { font, weightRegular, weightSemiBold } from 'src/components/css'
import type { CalendarWeekGridProps } from './types'
import CalendarGameCard from './CalendarGameCard'
import {
  buildWeekDays,
  formatCivilDay,
  todayCivilDate,
} from './buildCalendarDays'
import { useAnchorDate, useRangeChangeNotifier } from './useAnchorDate'
import { calendarLabel } from './calendarI18n'

const CalendarWeekGrid = ({
  items,
  isSoccer,
  isHockey,
  loading,
  onWeekChange,
  anchorDate,
  onAnchorDateChange,
}: CalendarWeekGridProps) => {
  'use memo'

  const { anchor, setValue } = useAnchorDate(anchorDate, onAnchorDateChange)

  const weekStart = anchor.minus({ days: anchor.weekday % 7 })
  const weekEndExclusive = weekStart.plus({ days: 7 })

  useRangeChangeNotifier(
    weekStart.toFormat('yyyy-MM-dd'),
    weekEndExclusive.toFormat('yyyy-MM-dd'),
    onWeekChange
  )

  const goToWeek = (offset: number) =>
    setValue(weekStart.plus({ weeks: offset }).toFormat('yyyy-MM-dd'))

  const weekDays = buildWeekDays(anchor, items)
  const weekTitle = `${weekStart.toFormat('LLL d')} – ${weekEndExclusive
    .minus({ days: 1 })
    .toFormat('LLL d, yyyy')}`

  return (
    <div css={containerStyles} data-calendar-grid>
      <div css={navStyles}>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToWeek(-1)}
          aria-label={calendarLabel('calendarPrevWeek')}
        >
          &#8249;
        </button>
        <span css={titleStyles}>{weekTitle}</span>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToWeek(1)}
          aria-label={calendarLabel('calendarNextWeek')}
        >
          &#8250;
        </button>
        <button
          type='button'
          css={todayBtnStyles}
          onClick={() => setValue(todayCivilDate())}
          aria-label={calendarLabel('calendarGoToThisWeek')}
        >
          {calendarLabel('calendarToday')}
        </button>
      </div>

      {loading ? (
        <div css={loadingStyles}>
          <Spin size='small' />
        </div>
      ) : (
        //  Columns stay up for an empty week — the structure IS the information.
        <>
          <div css={headerRowStyles}>
            {weekDays.map((day) => (
              <div key={day.dayKey} css={headerCellStyles(day.isToday)}>
                {formatCivilDay(day.dayKey, 'ccc LLL d')}
              </div>
            ))}
          </div>

          <div css={gridStyles}>
            {weekDays.map((day) => {
              const ordered = [...day.tbdGames, ...day.games]
              return (
                <div key={day.dayKey} css={columnStyles(day.isToday)}>
                  {ordered.length === 0 ? (
                    <div css={emptyDayStyles}>
                      {calendarLabel('calendarNoGames')}
                    </div>
                  ) : (
                    ordered.map((game) => (
                      <CalendarGameCard
                        key={game.id}
                        game={game}
                        isSoccer={isSoccer}
                        isHockey={isHockey}
                      />
                    ))
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  background: ${colors.SECONDARY_500};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const navStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 14px 16px;
  background: ${colors.SECONDARY_300};
`

const navBtnStyles = css`
  ${font.body}
  background: transparent;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
    color: ${colors.NEUTRAL_0};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const todayBtnStyles = css`
  ${navBtnStyles};
  font-size: 13px;
  padding: 4px 12px;
  margin-left: 8px;
`

//  Body font — see the note on `monthTitleStyles` in CalendarGrid. The three
//  period-nav titles have to match each other, or switching sub-view changes
//  typeface mid-calendar.
const titleStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 18px;
  color: ${colors.NEUTRAL_0};
  min-width: 260px;
  text-align: center;
`

const headerRowStyles = css`
  display: flex;
  background: ${colors.SECONDARY_400};
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const headerCellStyles = (isToday: boolean) => css`
  ${font.body}
  ${weightSemiBold}
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: ${isToday ? colors.PRIMARY : colors.NEUTRAL_70};
  text-align: center;
  padding: 10px 8px;
  border-right: 1px solid ${colors.SECONDARY_200};
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &:last-of-type {
    border-right: none;
  }
`

const gridStyles = css`
  display: flex;
  background: ${colors.SECONDARY_500};
`

/**
 * The "today" wash is derived from the tenant PRIMARY rather than from a
 * hardcoded yellow. The literal this replaced was the SportNinja flair colour
 * and would have bled into the Canlan build.
 */
const columnStyles = (isToday: boolean) => css`
  flex: 1;
  min-width: 0;
  min-height: 300px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid ${colors.SECONDARY_200};
  overflow-y: auto;
  background: ${isToday ? colors.SECONDARY_400 : 'transparent'};
  ${isToday ? `box-shadow: inset 0 2px 0 ${colors.PRIMARY};` : ''}

  &:last-of-type {
    border-right: none;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.NEUTRAL_100};
    border-radius: 2px;
  }
`

const loadingStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  background: ${colors.SECONDARY_500};
`

const emptyDayStyles = css`
  ${font.body}
  ${weightRegular}
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
  text-align: center;
  padding: 16px 4px;
`

export default CalendarWeekGrid
